import {
  addMonths,
  differenceInCalendarMonths,
  format,
  max,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns';

import { MIN_DATE } from 'utils/dates';
import { createSelector } from 'reselect';

// The Monthly Trend charts plot the selected month and this many full calendar
// months before it.
export const TREND_PRIOR_MONTHS = 3;

const monthKey = (date) => format(date, 'yyyy-MM');

// Nothing before MIN_DATE is shown, so the trend window is cut short there
// (a February selection plots only January and February).
const trendStart = (date) =>
  max([
    startOfMonth(subMonths(new Date(date), TREND_PRIOR_MONTHS)),
    startOfMonth(parseISO(MIN_DATE)),
  ]);

export const getFinancialReport = (state) => state.financialReport;

export const getFinancialReportDateRange = createSelector(
  getFinancialReport,
  ({ dateRange }) => dateRange
);

// The request widens the selected period back to the start of the trend
// window, so one response feeds both the comparison and the trend charts.
export const getFinancialReportFetchRange = createSelector(
  getFinancialReportDateRange,
  ({ startDate, endDate }) => ({
    startDate: startDate && trendStart(startDate),
    endDate,
  })
);

// The YYYY-MM keys of the selected period, which the comparison charts cover.
export const getFinancialReportPeriodMonthKeys = createSelector(
  getFinancialReportDateRange,
  ({ startDate, endDate }) => {
    const keys = [];
    if (!startDate || !endDate) return keys;
    const last = startOfMonth(new Date(endDate));
    for (
      let month = startOfMonth(new Date(startDate));
      month <= last;
      month = addMonths(month, 1)
    ) {
      keys.push(monthKey(month));
    }
    return keys;
  }
);

// The trend window: up to TREND_PRIOR_MONTHS months before the selected
// period's end month, then that month, oldest first.
export const getFinancialReportTrendMonths = createSelector(
  getFinancialReportDateRange,
  ({ endDate }) => {
    if (!endDate) return [];
    const last = startOfMonth(new Date(endDate));
    const first = trendStart(last);
    return Array.from({
      length: Math.max(differenceInCalendarMonths(last, first), 0) + 1,
    }).map((_, i) => {
      const month = addMonths(first, i);
      return { key: monthKey(month), label: format(month, 'MMM yyyy') };
    });
  }
);

export const getFinancialReportStudios = createSelector(
  getFinancialReport,
  ({ studioIds }) => studioIds
);

export const getLaborEfficiency = createSelector(
  getFinancialReport,
  ({ laborEfficiency }) => laborEfficiency
);

export const isLaborEfficiencyLoading = createSelector(
  getFinancialReport,
  ({ loading }) => loading
);

export const getLaborEfficiencyError = createSelector(
  getFinancialReport,
  ({ error }) => error
);
